import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { School, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
export default function ClassFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isEdit = Boolean(id);
    const [formData, setFormData] = useState({ name: '', displayName: '', gradeNumber: 6 });
    const { data: existingData, isLoading: loadingExisting } = useQuery({
        queryKey: ['class', id],
        queryFn: async () => api.get(`/api/classes/${id}`),
        enabled: isEdit,
    });
    useEffect(() => {
        if (existingData?.data) {
            const cls = existingData.data;
            setFormData({
                name: cls.name || '',
                displayName: cls.display_name || '',
                gradeNumber: cls.grade_number || 6,
            });
        }
    }, [existingData]);
    const saveMutation = useMutation({
        mutationFn: async (payload) => {
            if (isEdit)
                return api.patch(`/api/classes/${id}`, payload);
            return api.post('/api/classes', payload);
        },
        onSuccess: () => {
            toast.success(isEdit ? 'Class updated' : 'Class created');
            queryClient.invalidateQueries({ queryKey: ['classes'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
            navigate('/classes');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to save class');
        },
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };
    if (isEdit && loadingExisting) {
        return (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsx(Loader2, { className: "w-6 h-6 animate-spin text-purple-500" }) }));
    }
    return (_jsxs("div", { className: "space-y-4 sm:space-y-6", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "ghost", size: "icon", className: "h-10 w-10", onClick: () => navigate(-1), children: _jsx(ArrowLeft, { className: "w-5 h-5" }) }), _jsxs("div", { children: [_jsxs("h1", { className: "text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2", children: [_jsx(School, { className: "w-5 h-5 sm:w-6 sm:h-6 text-tangerine-500" }), isEdit ? 'Edit Class' : 'Add Class'] }), _jsx("p", { className: "text-xs sm:text-sm text-light-text-muted mt-1", children: isEdit ? 'Update class details' : 'Create a new class' })] })] }), _jsx("div", { className: "glass-card p-4 sm:p-6 rounded-2xl", children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium text-light-text dark:text-dark-text mb-1", children: "Class Name" }), _jsx(Input, { type: "text", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), placeholder: "e.g., Class 6", required: true, className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium text-light-text dark:text-dark-text mb-1", children: "Display Name" }), _jsx(Input, { type: "text", value: formData.displayName, onChange: (e) => setFormData({ ...formData, displayName: e.target.value }), placeholder: "e.g., VI Standard", className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium text-light-text dark:text-dark-text mb-1", children: "Grade Number" }), _jsx(Input, { type: "number", min: "6", max: "12", value: formData.gradeNumber, onChange: (e) => setFormData({ ...formData, gradeNumber: parseInt(e.target.value) }), required: true, className: "h-10" })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsx(Button, { type: "button", variant: "outline", className: "flex-1 h-10", onClick: () => navigate(-1), children: "Cancel" }), _jsx(Button, { type: "submit", disabled: saveMutation.isPending, className: "flex-1 h-10", children: saveMutation.isPending ? (_jsx(Loader2, { className: "w-4 h-4 animate-spin" })) : (isEdit ? 'Update' : 'Create') })] })] }) })] }));
}
